import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import { deleteScenario } from '../../../../actions'
import { Logger, LoggerTypes } from '../../../../utils'
import { AccessControl, AccessControlNoRights, Button, buttonModes } from '../../../../components'
import { userRightTypes } from '../../../../constants'

export const ScenarioDialogDelete = forwardRef(({ pid, scenario, openDialog, setOpenDialog }, ref) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const { t } = useTranslation()

  const onClickCancel = (e) => {
    e.preventDefault()
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioDialogDelete.onClickCancel()')
    setOpenDialog(!openDialog)
  }

  const onClickDelete = (e) => {
    e.preventDefault()
    e.stopPropagation()

    Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioDialogDelete.onCloseClick()', { pid, scenario })

    dispatch(deleteScenario(pid, scenario))
      .then((res) => {
        Logger.trace(LoggerTypes.CONTROLLER, 'ScenarioDialogDelete.onClickDelete().deleteScenario().then()', {
          scenario,
          res
        })

        if (res?.data?.isAxiosError) {
          enqueueSnackbar(t('common.api_failure'), { variant: 'error' })
        }
        setOpenDialog(!openDialog)
      })
      .catch((res) => {
        Logger.trace(LoggerTypes.WARN, 'ScenarioDialogDelete.onClickDelete().deleteScenario().catch()', { res })

        const error = (res && res.response && res.response.data && JSON.stringify(res.response.data)) || ''
        enqueueSnackbar(`${t('common.api_failure')} ${error}`, { variant: 'error' })
      })
  }

  Logger.trace(LoggerTypes.CONTAINER, 'ScenarioDialogDelete.render()', { openDialog, pid, scenario })

  return (
    <Dialog ref={ref} open={openDialog} onClose={onClickCancel} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">{`${t('scenarios.dialogs.delete.title')} ${scenario.name}`}</DialogTitle>
      <DialogContent>
        <AccessControl
          userPermissions={[userRightTypes.SCENARIO_DELETE]}
          renderNoAccess={() => <AccessControlNoRights />}
        >
          <DialogContentText>{t('scenarios.dialogs.delete.description')}</DialogContentText>
        </AccessControl>
      </DialogContent>
      <DialogActions>
        <Button
          mode={buttonModes.SECONDARY_ACTION}
          text={t('common.cancel')}
          icon={<CloseIcon />}
          onClick={onClickCancel}
        />
        <AccessControl userPermissions={[userRightTypes.SCENARIO_DELETE]} renderNoAccess={() => <></>}>
          <Button
            mode={buttonModes.PRIMARY_ACTION}
            text={t('common.delete')}
            icon={<DeleteOutlineIcon />}
            onClick={onClickDelete}
          />
        </AccessControl>
      </DialogActions>
    </Dialog>
  )
})

ScenarioDialogDelete.displayName = 'ScenarioDialogDelete'

ScenarioDialogDelete.propTypes = {
  pid: PropTypes.string.isRequired,
  scenario: PropTypes.object.isRequired,
  openDialog: PropTypes.bool.isRequired,
  setOpenDialog: PropTypes.func.isRequired
}

ScenarioDialogDelete.defaultProps = {
  pid: '1',
  scenario: {},
  openDialog: false,
  setOpenDialog: () => {}
}
